
import React from "react";
import { Bell, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ProfileFormValues } from "@/hooks/profile";

interface NotificationSettingsProps {
  form: UseFormReturn<ProfileFormValues>;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ form }) => {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-4 flex items-center">
        <Bell className="h-5 w-5 mr-2" />
        Notification Settings
      </h2>
      
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="email_notifications"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Email Notifications</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Receive email notifications about activity related to your reagents.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="daily_digest_email"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 ml-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Daily Digest</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Receive a daily email with new reagents and requests.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!form.watch("email_notifications")}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="weekly_digest_email"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 ml-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Weekly Digest</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Receive a weekly email every Monday with updates from the past 7 days.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!form.watch("email_notifications")}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="notify_swap_offers"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 ml-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Swap Offers</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Get notified when someone makes an offer for your reagents.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!form.watch("email_notifications")}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="notify_swap_responses"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 ml-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Swap Responses</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Get notified when your swap requests are approved or rejected.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!form.watch("email_notifications")}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="notify_messages"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 ml-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">New Messages</FormLabel>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Get notified when you receive new messages from other users.
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!form.watch("email_notifications")}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default NotificationSettings;
